import { useState, useEffect } from "react";
import { PageHeader } from "@/components/layout/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { fetchPaginated } from '@/lib/supabase-utils';
import { scopeRowsToTeamMembers } from '@/lib/team-scope';
import { useAuth } from "@/providers/auth-provider";
import { useTeamMemberFilter } from '@/hooks/use-team-member-filter';
import { 
  Download, 
  Users, 
  Calendar,
  FileSpreadsheet,
  RefreshCw,
  AlertCircle,
  TrendingUp,
  Radio
} from "lucide-react";
import { format, eachDayOfInterval } from "date-fns";
import { getTodayQatar, getQatarDayRange, getQatarWeekRange, getQatarMonthRange, getLastQatarWeekRange, getLastQatarMonthRange, getQatarRangeFromDates, utcToQatarDateStr, parseDateStrSafe } from '@/utils/timezone';
import { getLastScreenshotByTimeLogId, getReportableTimeLogIntervals, mergeTimeIntervals, splitIntervalByQatarDay, type TimeInterval } from "@/lib/time-utils";
import { fetchSessionScreenshotEvidence } from '@/lib/screenshot-evidence';
import { calculatePayableTimeSummary, type IdlePeriod } from '@/lib/payable-time';
import { formatEmployeeId, formatPosition } from "@/lib/employee-display";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";

interface DailyHours {
  [date: string]: number;
}

interface EmployeeData {
  id: string;
  name: string;
  email: string;
  role: string;
  staffId: string | null;
  position: string | null;
  dailyHours: DailyHours;
  totalHours: number;
  payableHours: number;
  idleHours: number;
  productivity: number; // percentage
  hasScreenshots?: boolean; // Track if user has screenshots
  estimatedHours?: number; // Track estimated hours from screenshots
  hasOngoingSession?: boolean; // Track if user has an active ongoing session
}

interface ReportUserRow {
  id: string;
  full_name: string | null;
  email: string;
  role: string;
  staff_id?: string | null;
  position?: string | null;
}

interface ReportStats {
  totalEmployees: number;
  totalPayableHours: number;
  totalIdleHours: number;
  averageProductivity: number;
  topPerformer: string;
}

export default function AllEmployeeReport() {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<EmployeeData[]>([]);
  const [dateRange, setDateRange] = useState("week");
  const [dateLabels, setDateLabels] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const reportStartDate = parseDateStrSafe(utcToQatarDateStr(startDate.toISOString()));
  const reportEndDate = parseDateStrSafe(utcToQatarDateStr(new Date(endDate.getTime() - 1).toISOString()));
  const [stats, setStats] = useState<ReportStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>();
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>();
  const [customPickerOpen, setCustomPickerOpen] = useState(false);
  const { userDetails, isSuperAdmin } = useAuth();
  const { teamMemberIds, loading: teamMembersLoading } = useTeamMemberFilter();
  const canView = userDetails?.role === 'admin' || userDetails?.role === 'manager' || userDetails?.role === 'team_leader';
  const { toast } = useToast();
  const organizationId = userDetails?.organization_id;

  useEffect(() => {
    if (canView && !teamMembersLoading) {
      if (dateRange === 'custom' && (!customStartDate || !customEndDate)) return;
      loadEmployeeData();
    }
  }, [dateRange, userDetails, organizationId, isSuperAdmin, customStartDate, customEndDate, teamMemberIds, teamMembersLoading]);

  const getDateRange = () => {
    const toRange = (r: { start: string; end: string }) => ({ start: new Date(r.start), end: new Date(r.end) });
    switch (dateRange) {
      case "today":
        return toRange(getQatarDayRange(getTodayQatar()));
      case "week":
        return toRange(getQatarWeekRange(0));
      case "month":
        return toRange(getQatarMonthRange());
      case "last-week":
        return toRange(getLastQatarWeekRange(0));
      case "last-month":
        return toRange(getLastQatarMonthRange());
      case "custom":
        if (customStartDate && customEndDate) {
          return toRange(getQatarRangeFromDates(customStartDate, customEndDate));
        }
        return toRange(getQatarWeekRange(0));
      default:
        return toRange(getQatarWeekRange(0));
    }
  };

  const loadEmployeeData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { start, end } = getDateRange();
      setStartDate(start);
      setEndDate(end);

      // Generate date labels for table headers
      const days = eachDayOfInterval({
        start: parseDateStrSafe(utcToQatarDateStr(start.toISOString())),
        end: parseDateStrSafe(utcToQatarDateStr(new Date(end.getTime() - 1).toISOString())),
      });
      const labels = days.map(day => format(day, 'EEE\nM/d'));
      setDateLabels(labels);

      // Some existing organizations predate the optional employment-profile
      // fields migration. Retry with the live base schema so reporting remains
      // available while still using position where that migration is present.
      const fetchUsers = async (columns: string): Promise<{
        data: ReportUserRow[] | null;
        error: { code?: string; message: string } | null;
      }> => {
        let usersQuery = supabase
          .from('users')
          .select(columns)
          .in('role', ['employee', 'admin', 'manager']);

        if (organizationId && !isSuperAdmin) {
          usersQuery = usersQuery.eq('organization_id', organizationId);
        }

        const { data, error } = await usersQuery.order('full_name');
        return {
          data: data as unknown as ReportUserRow[] | null,
          error,
        };
      };

      let { data: usersData, error: usersError } = await fetchUsers(
        'id, full_name, email, role, position'
      );

      const positionColumnMissing =
        usersError?.code === '42703' ||
        /(?:users\.)?position.*does not exist/i.test(usersError?.message || '');

      if (positionColumnMissing) {
        console.warn('users.position is unavailable; loading report with the base users schema');
        ({ data: usersData, error: usersError } = await fetchUsers(
          'id, full_name, email, role'
        ));
      }

      if (usersError) {
        console.error('Error fetching users:', usersError);
        throw new Error(`Failed to fetch users: ${usersError.message}`);
      }

      // Filter by team member IDs if team leader
      const filteredUsersData = scopeRowsToTeamMembers(usersData || [], teamMemberIds);

      if (!filteredUsersData || filteredUsersData.length === 0) {
        setEmployees([]);
        setStats(null);
        return;
      }

      // Pre-compute org user IDs so we can scope all high-volume queries to this org
      const orgUserIds = filteredUsersData.map(u => u.id);

      // Fetch time logs with pagination to bypass PostgREST row cap
      const timeLogsData = await fetchPaginated<any>(
        supabase
          .from('time_logs')
          .select(`
            id,
            user_id,
            start_time,
            end_time,
            is_idle,
            idle_seconds,
            deducted_seconds,
            status
          `)
          .lt('start_time', end.toISOString())
          .or(`end_time.gte.${start.toISOString()},end_time.is.null`)
          .in('user_id', orgUserIds)
          .order('start_time')
      );

      // Also get screenshots for the date range to include users with screenshots but no time logs
      const screenshotsData = await fetchPaginated<any>(
        supabase
          .from('screenshots')
          .select('user_id, time_log_id, captured_at, image_deleted_at, activity_percent')
          .gte('captured_at', start.toISOString())
          .lt('captured_at', end.toISOString())
          .in('user_id', orgUserIds)
          .order('captured_at')
      );

      // CRITICAL: Get idle_logs to calculate actual idle time (not just is_idle flag)
      const idleLogsData = await fetchPaginated<any>(
        supabase
          .from('idle_logs')
          .select('user_id, duration_seconds, idle_start, idle_end')
          .lte('idle_start', end.toISOString())
          .or(`idle_end.gte.${start.toISOString()},idle_end.is.null`)
          .in('user_id', orgUserIds)
      );

      const idleLogsByUser: { [userId: string]: IdlePeriod[] } = {};
      (idleLogsData || []).forEach(idleLog => {
        if (!idleLog.user_id || !idleLog.idle_start) return;
        if (!idleLogsByUser[idleLog.user_id]) idleLogsByUser[idleLog.user_id] = [];
        idleLogsByUser[idleLog.user_id].push({
          idle_start: idleLog.idle_start,
          idle_end: idleLog.idle_end,
        });
      });

      // Process data to create employee daily hours with productivity metrics
      const employeeMap: { [userId: string]: EmployeeData } = {};

      // Initialize all employees
      filteredUsersData.forEach(user => {
        const dailyHours: DailyHours = {};
        days.forEach(day => {
          dailyHours[format(day, 'yyyy-MM-dd')] = 0;
        });

        employeeMap[user.id] = {
          id: user.id,
          name: user.full_name || 'Unknown',
          email: user.email,
          role: user.role,
          staffId: (user as any).staff_id ?? null,
          position: (user as any).position ?? null,
          dailyHours,
          totalHours: 0,
          payableHours: 0,
          idleHours: 0,
          productivity: 0,
          hasScreenshots: false,  // Track if user has screenshots
          estimatedHours: 0,      // Track estimated hours from screenshots
          hasOngoingSession: false // Track if user has an active ongoing session
        };
      });

      // Process time logs with the same interval rules used by the employee report.
      const nowMs = Date.now();
      const rangeStartMs = start.getTime();
      const rangeEndMs = end.getTime();

      const sessionEvidence = await fetchSessionScreenshotEvidence(supabase, timeLogsData || [], {
        rangeStartMs, rangeEndMs, nowMs,
      });
      const screenshotsBySession = getLastScreenshotByTimeLogId(timeLogsData || [], sessionEvidence, {
        rangeStartMs, rangeEndMs, nowMs,
      });

      const logById = new Map((timeLogsData || []).map(log => [log.id, log]));
      const reportableIntervals = getReportableTimeLogIntervals(
        (timeLogsData || []).map(log => ({
          id: log.id,
          user_id: log.user_id,
          start_time: log.start_time,
          end_time: log.end_time,
          idle_seconds: log.idle_seconds,
          deducted_seconds: log.deducted_seconds,
          status: log.status,
        })),
        { rangeStartMs, rangeEndMs, nowMs, screenshotsBySession }
      );

      // Group intervals by user and by user+day for merging.
      const userIntervals: { [userId: string]: (TimeInterval & { logId?: string })[] } = {};
      const userDayIntervals: { [key: string]: TimeInterval[] } = {};
      const userOngoing: { [userId: string]: boolean } = {};

      reportableIntervals.forEach(interval => {
        if (!employeeMap[interval.userId]) return;
        if (!userIntervals[interval.userId]) userIntervals[interval.userId] = [];
        userIntervals[interval.userId].push(interval);

        const sourceLog = interval.logId ? logById.get(interval.logId) : null;
        if (sourceLog && !sourceLog.end_time) userOngoing[interval.userId] = true;

        splitIntervalByQatarDay(interval.startMs, interval.endMs, rangeStartMs, rangeEndMs).forEach(part => {
          const key = `${interval.userId}|${part.date}`;
          if (!userDayIntervals[key]) userDayIntervals[key] = [];
          userDayIntervals[key].push({ startMs: part.startMs, endMs: part.endMs });
        });
      });

      // Merge and aggregate per user per day
      for (const [key, intervals] of Object.entries(userDayIntervals)) {
        const [userId, logDate] = key.split('|');
        if (!employeeMap[userId] || employeeMap[userId].dailyHours[logDate] === undefined) continue;

        const merged = mergeTimeIntervals(intervals);
        let dayHours = 0;
        for (const interval of merged) {
          const hours = (interval.endMs - interval.startMs) / (1000 * 60 * 60);
          dayHours += hours;
        }

        employeeMap[userId].dailyHours[logDate] += dayHours;
        employeeMap[userId].totalHours += dayHours;

        if (userOngoing[userId]) {
          employeeMap[userId].hasOngoingSession = true;
        }
      }

      // Process screenshots to mark users who have activity even without time logs.
      (screenshotsData || []).forEach(screenshot => {
        if (!screenshot.user_id || !employeeMap[screenshot.user_id]) return;

        employeeMap[screenshot.user_id].hasScreenshots = true;
        
        // Screenshots prove activity evidence only. Logged hours must stay exact from time_logs.
      });

      // Calculate the same payroll-facing tracked, idle, and payable totals shown to employees.
      Object.keys(employeeMap).forEach(userId => {
        const summary = calculatePayableTimeSummary(
          userIntervals[userId] || [],
          idleLogsByUser[userId] || [],
          {
            rangeStartMs,
            rangeEndMs,
            nowMs,
            fallbackTimeLogs: (timeLogsData || []).filter(log => log.user_id === userId),
          }
        );
        employeeMap[userId].totalHours = summary.trackedSeconds / 3600;
        employeeMap[userId].idleHours = summary.idleSeconds / 3600;
        employeeMap[userId].payableHours = summary.payableSeconds / 3600;
      });

      // Calculate productivity percentages
      Object.values(employeeMap).forEach(employee => {
        if (employee.totalHours > 0) {
          employee.productivity = Math.round((employee.payableHours / employee.totalHours) * 100);
        }
      });

      // Convert to array and sort by total hours
      // Show ALL organization members, even those without activity data
      const employeeArray = Object.values(employeeMap)
        .sort((a, b) => b.totalHours - a.totalHours);

      setEmployees(employeeArray);

      // Calculate overall statistics
      const totalEmployees = employeeArray.length;
      const totalPayableHours = employeeArray.reduce((sum, emp) => sum + emp.payableHours, 0);
      const totalIdleHours = employeeArray.reduce((sum, emp) => sum + emp.idleHours, 0);
      const averageProductivity = totalEmployees > 0 
        ? Math.round(employeeArray.reduce((sum, emp) => sum + emp.productivity, 0) / totalEmployees)
        : 0;
      const topPerformer = employeeArray.length > 0 ? employeeArray[0].name : 'None';

      setStats({
        totalEmployees,
        totalPayableHours,
        totalIdleHours,
        averageProductivity,
        topPerformer
      });

    } catch (error: any) {
      console.error('Error loading employee data:', error);
      setError(error.message || 'An unexpected error occurred');
      toast({
        title: "Error loading report",
        description: error.message || 'Failed to load employee data',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatHours = (hours: number): string => {
    if (hours === 0) return "0:00:00";
    const totalSeconds = Math.floor(hours * 3600);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const exportToCSV = () => {
    if (employees.length === 0) {
      toast({
        title: "No data to export",
        description: "Please ensure there is employee data to export",
        variant: "destructive",
      });
      return;
    }

    const headers = [
      'Employee ID',
      'Member',
      'Position',
      ...dateLabels.map(label => label.replace('\n', ' ')),
      'Total Hours',
      'Idle Time',
      'Payable Hours',
      'Productivity %'
    ];

    const rows = employees.map(emp => [
      formatEmployeeId(emp.staffId, emp.id),
      emp.name,
      formatPosition(emp.position),
      ...dateLabels.map((_, index) => {
        const dateKey = format(eachDayOfInterval({ start: startDate, end: endDate })[index], 'yyyy-MM-dd');
        return formatHours(emp.dailyHours[dateKey] || 0);
      }),
      formatHours(emp.totalHours),
      formatHours(emp.idleHours),
      formatHours(emp.payableHours),
      `${emp.productivity}%`
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all-employee-report-${format(reportStartDate, 'yyyy-MM-dd')}-to-${format(reportEndDate, 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast({
      title: "Export successful",
      description: "Employee report has been downloaded",
    });
  };

  const getTotalHoursForDay = (dayIndex: number): number => {
    const dateKey = format(eachDayOfInterval({ start: startDate, end: endDate })[dayIndex], 'yyyy-MM-dd');
    return employees.reduce((total, emp) => total + (emp.dailyHours[dateKey] || 0), 0);
  };

  const getGrandTotal = (): number => {
    return employees.reduce((total, emp) => total + emp.totalHours, 0);
  };

  const getProductivityColor = (productivity: number): string => {
    if (productivity >= 80) return 'text-green-600 bg-green-100';
    if (productivity >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (!canView) {
    return (
      <div className="container py-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-yellow-500" />
            <p className="text-muted-foreground">Access denied. Admin privileges required.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-6">
      <PageHeader
        title="All Employee Report"
        subtitle="Daily hours breakdown and productivity metrics for all employees"
      />

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Select value={dateRange} onValueChange={(value) => {
            setDateRange(value);
            if (value === 'custom') {
              setCustomPickerOpen(true);
            }
          }}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="last-week">Last Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="last-month">Last Month</SelectItem>
              <SelectItem value="custom">Custom Dates</SelectItem>
            </SelectContent>
          </Select>

          {dateRange === 'custom' && (
            <Popover open={customPickerOpen} onOpenChange={setCustomPickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Calendar className="h-3.5 w-3.5" />
                  {customStartDate && customEndDate
                    ? `${format(customStartDate, 'MMM d')} - ${format(customEndDate, 'MMM d, yyyy')}`
                    : 'Pick dates'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-4" align="start">
                <div className="flex gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Start Date</Label>
                    <CalendarPicker
                      mode="single"
                      selected={customStartDate}
                      onSelect={(date) => {
                        setCustomStartDate(date);
                        if (date && customEndDate && date > customEndDate) {
                          setCustomEndDate(undefined);
                        }
                      }}
                      initialFocus
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">End Date</Label>
                    <CalendarPicker
                      mode="single"
                      selected={customEndDate}
                      onSelect={(date) => {
                        setCustomEndDate(date);
                        if (date && customStartDate) {
                          setCustomPickerOpen(false);
                        }
                      }}
                      disabled={(date) => customStartDate ? date < customStartDate : false}
                    />
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}
          
          <Badge variant="outline" className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {format(reportStartDate, 'MMM d')} - {format(reportEndDate, 'MMM d, yyyy')}
          </Badge>

          {stats && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              {stats.averageProductivity}% Avg Productivity
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={loadEmployeeData}
            disabled={loading}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Button
            onClick={exportToCSV}
            disabled={loading || employees.length === 0}
            variant="outline"
            size="sm"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {error && (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle className="h-4 w-4" />
              <span className="font-medium">Error:</span>
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Employee Hours Summary {loading ? '' : `(${employees.length} users)`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin mr-2" />
              Loading employee data...
            </div>
          ) : employees.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No employee data found for the selected period.</p>
              <div className="text-sm mt-2">Try selecting a different date range or ensure users have logged time.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-medium bg-muted/30">Employee ID</th>
                    <th className="text-left p-3 font-medium bg-muted/30">Member</th>
                    <th className="text-left p-3 font-medium bg-muted/30">Position</th>
                    {dateLabels.map((label, index) => (
                      <th key={index} className="text-center p-2 font-medium bg-muted/30 min-w-[80px]">
                        <div className="whitespace-pre-line text-xs">
                          {label}
                        </div>
                      </th>
                    ))}
                    <th className="text-center p-3 font-medium bg-muted/30">Total</th>
                    <th className="text-center p-3 font-medium bg-muted/30">Idle Time</th>
                    <th className="text-center p-3 font-medium bg-muted/30">Payable Hours</th>
                    <th className="text-center p-3 font-medium bg-muted/30">Productivity</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((employee, empIndex) => (
                    <tr key={employee.id} className={empIndex % 2 === 0 ? 'bg-muted/10' : ''}>
                      <td className="p-3 font-mono text-xs text-muted-foreground">
                        {formatEmployeeId(employee.staffId, employee.id)}
                      </td>
                      <td className="p-3 font-medium">
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {employee.name}
                            <Badge variant="outline" className="text-xs capitalize">
                              {employee.role}
                            </Badge>
                            {employee.hasOngoingSession && (
                              <Badge className="text-xs bg-green-500 hover:bg-green-600 text-white animate-pulse">
                                <Radio className="h-3 w-3 mr-1" />
                                Live
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">{employee.email}</div>
                          {employee.estimatedHours && employee.estimatedHours > 0 && (
                            <div className="text-xs text-orange-600 mt-1">
                              📸 Estimated from screenshots
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-sm">
                        {formatPosition(employee.position)}
                      </td>
                      {dateLabels.map((_, dayIndex) => {
                        const dateKey = format(eachDayOfInterval({ start: startDate, end: endDate })[dayIndex], 'yyyy-MM-dd');
                        const hours = employee.dailyHours[dateKey] || 0;
                        return (
                          <td key={dayIndex} className="text-center p-2 text-sm">
                            <span className={hours > 0 ? 'font-medium' : 'text-muted-foreground'}>
                              {formatHours(hours)}
                            </span>
                          </td>
                        );
                      })}
                      <td className="text-center p-3 font-bold text-primary">
                        {formatHours(employee.totalHours)}
                      </td>
                      <td className="text-center p-3 font-medium text-orange-600">
                        {formatHours(employee.idleHours)}
                      </td>
                      <td className="text-center p-3 font-bold text-green-600">
                        {formatHours(employee.payableHours)}
                      </td>
                      <td className="text-center p-3">
                        <Badge 
                          variant="secondary" 
                          className={getProductivityColor(employee.productivity)}
                        >
                          {employee.productivity}%
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  
                  {/* Totals Row */}
                  <tr className="border-t-2 bg-muted/20 font-bold">
                    <td className="p-3"></td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4" />
                        TOTALS
                      </div>
                    </td>
                    <td className="p-3"></td>
                    {dateLabels.map((_, dayIndex) => (
                      <td key={dayIndex} className="text-center p-2 font-bold text-primary">
                        {formatHours(getTotalHoursForDay(dayIndex))}
                      </td>
                    ))}
                    <td className="text-center p-3 font-bold text-primary text-lg">
                      {formatHours(getGrandTotal())}
                    </td>
                    <td className="text-center p-3 font-bold text-orange-600 text-lg">
                      {formatHours(stats?.totalIdleHours || 0)}
                    </td>
                    <td className="text-center p-3 font-bold text-green-600 text-lg">
                      {formatHours(stats?.totalPayableHours || 0)}
                    </td>
                    <td className="text-center p-3">
                      <Badge variant="outline" className="font-bold">
                        {stats?.averageProductivity || 0}%
                      </Badge>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Summary Cards */}
      {!loading && employees.length > 0 && stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-6">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-primary">{stats.totalEmployees}</div>
              <div className="text-sm text-muted-foreground">Active Employees</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-primary">{formatHours(stats.totalPayableHours)}</div>
              <div className="text-sm text-muted-foreground">Total Payable Hours</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-primary">{formatHours(stats.totalIdleHours)}</div>
              <div className="text-sm text-muted-foreground">Total Idle Hours</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-primary">{stats.averageProductivity}%</div>
              <div className="text-sm text-muted-foreground">Avg Productivity</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-lg font-bold text-primary truncate" title={stats.topPerformer}>
                {stats.topPerformer}
              </div>
              <div className="text-sm text-muted-foreground">Top Performer</div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
